import React, { useEffect, useState } from 'react';
import * as XLSX from 'xlsx';
import { childrenMapping, parentsMapping, childrenQuestionsList, parentsQuestionsList } from './questionsConfig';

const PollResults = ({ foodBlockCode, onClose }) => {
  const [activeTab, setActiveTab] = useState('children');
  const [childrenData, setChildrenData] = useState(null);
  const [parentsData, setParentsData] = useState(null);
  const [loading, setLoading] = useState({ children: true, parents: true });
  const [error, setError] = useState({ children: null, parents: null });
  const [showDetails, setShowDetails] = useState(false);
  const [showMetrics, setShowMetrics] = useState(true);

  // Блокировка прокрутки фона при открытии модального окна
  useEffect(() => {
    const scrollY = window.scrollY;
    const originalStyle = document.body.style.overflow;
    
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.classList.add('modal-open');
    
    return () => {
      document.body.style.overflow = originalStyle;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.classList.remove('modal-open');
      window.scrollTo(0, scrollY);
    };
  }, []);

  // Загрузка данных для детей
  useEffect(() => {
    const fetchChildrenData = async () => {
      try {
        setLoading(prev => ({ ...prev, children: true }));
        setError(prev => ({ ...prev, children: null }));
        
        const response = await fetch(`https://api.cemon.ru/opros/opros2026/deti/pisheblok/?id=${foodBlockCode}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const jsonData = await response.json();
        
        if (jsonData && jsonData.length > 0) {
          setChildrenData(jsonData[0]);
        } else {
          setChildrenData(null);
          setError(prev => ({ ...prev, children: 'Данные детей не найдены для указанного пищеблока' }));
        }
      } catch (err) {
        console.error('Ошибка загрузки данных детей:', err);
        setChildrenData(null);
        setError(prev => ({ ...prev, children: err.message || 'Произошла ошибка при загрузке данных детей' }));
      } finally {
        setLoading(prev => ({ ...prev, children: false }));
      }
    };

    if (foodBlockCode) {
      fetchChildrenData();
    }
  }, [foodBlockCode]);

  // Загрузка данных для родителей
  useEffect(() => {
    const fetchParentsData = async () => {
      try {
        setLoading(prev => ({ ...prev, parents: true }));
        setError(prev => ({ ...prev, parents: null }));
        
        const response = await fetch(`https://api.cemon.ru/opros/opros2026/roditeli/?id=${foodBlockCode}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const jsonData = await response.json();
        
        if (jsonData && jsonData.length > 0) {
          setParentsData(jsonData[0]);
        } else {
          setParentsData(null);
          setError(prev => ({ ...prev, parents: 'Данные родителей не найдены для указанного пищеблока' }));
        }
      } catch (err) {
        console.error('Ошибка загрузки данных родителей:', err);
        setParentsData(null);
        setError(prev => ({ ...prev, parents: err.message || 'Произошла ошибка при загрузке данных родителей' }));
      } finally {
        setLoading(prev => ({ ...prev, parents: false }));
      }
    };

    if (foodBlockCode) {
      fetchParentsData();
    }
  }, [foodBlockCode]);

  // Закрытие по ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && onClose) {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [onClose]);

  // Закрытие по клику на фон
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && onClose) {
      onClose();
    }
  };

  // Остановка прокрутки внутри модального окна
  const handleModalScroll = (e) => {
    e.stopPropagation();
  };

  // Функция для получения процента (максимум 100%)
  const getPercentage = (value, total) => {
    if (!total || total === 0) return 0;
    const percent = (value / total) * 100;
    return Math.min(percent, 100).toFixed(1);
  };

  // Экспорт в Excel
  const exportToExcel = () => {
    const currentData = activeTab === 'children' ? childrenData : parentsData;
    const currentMapping = activeTab === 'children' ? childrenMapping : parentsMapping;
    const totalRespondents = currentData ? (currentData['Q0'] || 0) : 0;
    
    if (!currentData) {
      alert('Нет данных для экспорта');
      return;
    }

    try {
      const wb = XLSX.utils.book_new();
      const exportData = [];
      
      exportData.push(['РЕЗУЛЬТАТЫ ОПРОСА']);
      exportData.push([`Тип опроса: ${activeTab === 'children' ? 'Дети' : 'Родители'}`]);
      exportData.push([`Пищеблок: ${currentData.Пищеблок || foodBlockCode}`]);
      exportData.push([`Код пищеблока: ${foodBlockCode}`]);
      exportData.push([`Всего респондентов: ${totalRespondents}`]);
      exportData.push([`Дата экспорта: ${new Date().toLocaleDateString('ru-RU')}`]);
      exportData.push([]);
      exportData.push([]);
      
      const questionsList = activeTab === 'children' ? childrenQuestionsList : parentsQuestionsList;
      
      questionsList.forEach(questionKey => {
        const question = currentMapping[questionKey];
        if (!question) return;
        if (question.isTotal) return;
        
        if (question.isRating) {
          const ratingValue = currentData[questionKey];
          if (ratingValue !== undefined && ratingValue !== null && ratingValue !== '') {
            exportData.push([`ВОПРОС: ${question.text}`]);
            exportData.push([`Средний балл: ${typeof ratingValue === 'number' ? ratingValue.toFixed(2) : ratingValue}`]);
            exportData.push([]);
          }
          return;
        }
        
        const options = question.options;
        if (options && options.length > 0) {
          const answers = [];
          options.forEach((option, idx) => {
            const valueKey = `${questionKey}_${idx + 1}`;
            const value = currentData[valueKey];
            if (value !== undefined && value > 0) {
              const percent = getPercentage(value, totalRespondents);
              answers.push({ option, value, percent });
            }
          });
          
          if (answers.length === 0) return;
          
          exportData.push([`ВОПРОС: ${question.text}`]);
          exportData.push(['Вариант ответа', 'Количество', 'Процент']);
          answers.forEach(answer => {
            exportData.push([answer.option, answer.value, `${answer.percent}%`]);
          });
          exportData.push([]);
          return;
        }
        
        const value = currentData[questionKey];
        if (value !== undefined && value !== null && value !== '') {
          exportData.push([`ВОПРОС: ${question.text}`]);
          exportData.push([`Ответ: ${value}`]);
          exportData.push([]);
        }
      });
      
      const ws = XLSX.utils.aoa_to_sheet(exportData);
      ws['!cols'] = [{ wch: 50 }, { wch: 15 }, { wch: 15 }];
      
      XLSX.utils.book_append_sheet(wb, ws, `Результаты_${activeTab === 'children' ? 'Дети' : 'Родители'}`);
      
      const fileName = `опрос_${activeTab === 'children' ? 'дети' : 'родители'}_${foodBlockCode}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
    } catch (error) {
      console.error('Ошибка при экспорте:', error);
      alert('Произошла ошибка при экспорте данных');
    }
  };

  const renderMetrics = (data, totalRespondents, type) => {
    if (!data) return null;
    
    if (type === 'children') {
      const satisfiedCount = data['Q5_1'] || 0;
      const satisfiedPercent = getPercentage(satisfiedCount, totalRespondents);
      const dissatisfiedCount = data['Q5_3'] || 0;
      const dissatisfiedPercent = getPercentage(dissatisfiedCount, totalRespondents);
      const breakfastRating = data['Q9'] || 0;
      const lunchRating = data['Q10'] || 0;
      const fullnessCount = data['Q8_1'] || 0;
      const fullnessPercent = getPercentage(fullnessCount, totalRespondents);
      const buyAtBuffetCount = data['Q24_1'] || 0;
      const buyAtBuffetPercent = getPercentage(buyAtBuffetCount, totalRespondents);
      
      const breakfastOnly = data['Q4_1'] || 0;
      const lunchOnly = data['Q4_2'] || 0;
      const bothMeals = data['Q4_3'] || 0;
      const hotMealsCount = breakfastOnly + lunchOnly + bothMeals;
      const hotMealsPercent = getPercentage(hotMealsCount, totalRespondents);
      
      return (
        <div className="metrics-container">
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon">👥</div>
              <div className="metric-info">
                <div className="metric-value">{totalRespondents}</div>
                <div className="metric-label">респондентов</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">🍲</div>
              <div className="metric-info">
                <div className="metric-value">{hotMealsPercent}%</div>
                <div className="metric-label">горячее питание</div>
                <div className="metric-sub">{hotMealsCount} чел.</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">🥨</div>
              <div className="metric-info">
                <div className="metric-value">{buyAtBuffetPercent}%</div>
                <div className="metric-label">покупают в буфете</div>
                <div className="metric-sub">{buyAtBuffetCount} чел.</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">😊</div>
              <div className="metric-info">
                <div className="metric-value">{fullnessPercent}%</div>
                <div className="metric-label">чувствуют сытость</div>
                <div className="metric-sub">{fullnessCount} чел.</div>
              </div>
            </div>
          </div>
          <div className="metrics-grid">
            <div className="metric-card rating-card">
              <div className="metric-icon">⭐</div>
              <div className="metric-info">
                <div className="metric-value">{breakfastRating.toFixed(1)}</div>
                <div className="metric-label">оценка завтрака</div>
                <div className="metric-sub">из 5</div>
              </div>
            </div>
            <div className="metric-card rating-card">
              <div className="metric-icon">⭐</div>
              <div className="metric-info">
                <div className="metric-value">{lunchRating.toFixed(1)}</div>
                <div className="metric-label">оценка обеда</div>
                <div className="metric-sub">из 5</div>
              </div>
            </div>
            <div className="metric-card satisfaction">
              <div className="metric-icon">👍</div>
              <div className="metric-info">
                <div className="metric-value">{satisfiedPercent}%</div>
                <div className="metric-label">довольны</div>
                <div className="metric-sub">{satisfiedCount} чел.</div>
              </div>
            </div>
            <div className="metric-card dissatisfaction">
              <div className="metric-icon">👎</div>
              <div className="metric-info">
                <div className="metric-value">{dissatisfiedPercent}%</div>
                <div className="metric-label">недовольны</div>
                <div className="metric-sub">{dissatisfiedCount} чел.</div>
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      const interestedCount = data['Q5_1'] || 0;
      const interestedPercent = getPercentage(interestedCount, totalRespondents);
      const excellentHealth = data['Q6_1'] || 0;
      const goodHealth = data['Q6_2'] || 0;
      const goodHealthPercent = getPercentage(excellentHealth + goodHealth, totalRespondents);
      const individualNeed = data['Q7_1'] || 0;
      const individualNeedPercent = getPercentage(individualNeed, totalRespondents);
      const satisfiedCount = data['Q23_1'] || 0;
      const satisfiedPercent = getPercentage(satisfiedCount, totalRespondents);
      const dissatisfiedCount = data['Q23_3'] || 0;
      const dissatisfiedPercent = getPercentage(dissatisfiedCount, totalRespondents);
      const breakfastRating = data['Q11'] || 0;
      const lunchRating = data['Q21'] || 0;
      const participateControl = data['Q29_1'] || 0;
      const participatePercent = getPercentage(participateControl, totalRespondents);
      const surveyRegular = data['Q36_1'] || 0;
      const surveyPercent = getPercentage(surveyRegular, totalRespondents);
      
      return (
        <div className="metrics-container">
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon">👥</div>
              <div className="metric-info">
                <div className="metric-value">{totalRespondents}</div>
                <div className="metric-label">родителей</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">📊</div>
              <div className="metric-info">
                <div className="metric-value">{interestedPercent}%</div>
                <div className="metric-label">ежедневный интерес</div>
                <div className="metric-sub">{interestedCount} чел.</div>
              </div>
            </div>
            <div className="metric-card satisfaction">
              <div className="metric-icon">❤️</div>
              <div className="metric-info">
                <div className="metric-value">{goodHealthPercent}%</div>
                <div className="metric-label">хорошее здоровье</div>
                <div className="metric-sub">{excellentHealth + goodHealth} чел.</div>
              </div>
            </div>
            <div className="metric-card warning">
              <div className="metric-icon">🩺</div>
              <div className="metric-info">
                <div className="metric-value">{individualNeedPercent}%</div>
                <div className="metric-label">нуждаются в диете</div>
                <div className="metric-sub">{individualNeed} чел.</div>
              </div>
            </div>
          </div>
          <div className="metrics-grid">
            <div className="metric-card rating-card">
              <div className="metric-icon">⭐</div>
              <div className="metric-info">
                <div className="metric-value">{breakfastRating.toFixed(1)}</div>
                <div className="metric-label">оценка завтрака</div>
                <div className="metric-sub">из 5</div>
              </div>
            </div>
            <div className="metric-card rating-card">
              <div className="metric-icon">⭐</div>
              <div className="metric-info">
                <div className="metric-value">{lunchRating.toFixed(1)}</div>
                <div className="metric-label">оценка обеда</div>
                <div className="metric-sub">из 5</div>
              </div>
            </div>
            <div className="metric-card satisfaction">
              <div className="metric-icon">👍</div>
              <div className="metric-info">
                <div className="metric-value">{satisfiedPercent}%</div>
                <div className="metric-label">ребенок доволен</div>
                <div className="metric-sub">{satisfiedCount} чел.</div>
              </div>
            </div>
            <div className="metric-card dissatisfaction">
              <div className="metric-icon">👎</div>
              <div className="metric-info">
                <div className="metric-value">{dissatisfiedPercent}%</div>
                <div className="metric-label">ребенок недоволен</div>
                <div className="metric-sub">{dissatisfiedCount} чел.</div>
              </div>
            </div>
          </div>
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon">🔍</div>
              <div className="metric-info">
                <div className="metric-value">{participatePercent}%</div>
                <div className="metric-label">участвуют в контроле</div>
                <div className="metric-sub">{participateControl} чел.</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">📝</div>
              <div className="metric-info">
                <div className="metric-value">{surveyPercent}%</div>
                <div className="metric-label">регулярное анкетирование</div>
                <div className="metric-sub">{surveyRegular} чел.</div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  };

  const renderDetails = (data, totalRespondents, mapping, questionsList) => {
    if (!data) return null;
    
    return questionsList.map(questionKey => {
      const question = mapping[questionKey];
      if (!question) return null;
      if (question.isTotal) return null;
      
      if (question.isRating) {
        const ratingValue = data[questionKey];
        if (!ratingValue && ratingValue !== 0) return null;
        const stars = '★'.repeat(Math.round(ratingValue)) + '☆'.repeat(5 - Math.round(ratingValue));
        return (
          <div key={questionKey} className="detail-block">
            <div className="detail-question">{question.text}</div>
            <div className="detail-rating">
              <span className="rating-stars">{stars}</span>
              <span className="rating-value">{typeof ratingValue === 'number' ? ratingValue.toFixed(1) : ratingValue}</span>
            </div>
          </div>
        );
      }
      
      const options = question.options;
      if (options && options.length > 0) {
        const answers = [];
        options.forEach((option, idx) => {
          const valueKey = `${questionKey}_${idx + 1}`;
          const value = data[valueKey];
          if (value !== undefined && value > 0) {
            const percent = getPercentage(value, totalRespondents);
            answers.push({ option, value, key: valueKey, percent: parseFloat(percent) });
          }
        });
        
        answers.sort((a, b) => b.percent - a.percent);
        if (answers.length === 0) return null;
        
        return (
          <div key={questionKey} className="detail-block">
            <div className="detail-question">{question.text}</div>
            <div className="detail-answers">
              {answers.map((answer) => {
                const percent = answer.percent.toFixed(1);
                return (
                  <div key={answer.key} className="answer-item">
                    <div className="answer-header">
                      <span className="answer-text">{answer.option}</span>
                      <span className="answer-numbers">{percent}% ({answer.value})</span>
                    </div>
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${percent}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      }
      
      const value = data[questionKey];
      if (value !== undefined && value !== null && value !== '') {
        return (
          <div key={questionKey} className="detail-block simple">
            <div className="detail-question">{question.text}</div>
            <div className="detail-simple-answer">{value}</div>
          </div>
        );
      }
      
      return null;
    });
  };

  const currentData = activeTab === 'children' ? childrenData : parentsData;
  const currentError = activeTab === 'children' ? error.children : error.parents;
  const currentLoading = activeTab === 'children' ? loading.children : loading.parents;
  const currentMapping = activeTab === 'children' ? childrenMapping : parentsMapping;
  const currentQuestionsList = activeTab === 'children' ? childrenQuestionsList : parentsQuestionsList;
  const totalRespondents = currentData ? (currentData['Q0'] || 0) : 0;

  // Рендер основного модального окна (всегда одна структура)
  return (
    <div className="poll-results-overlay" onClick={handleBackdropClick}>
      <div className="poll-results-container" onWheel={handleModalScroll}>
        <div className="poll-results-header">
          <div>
            <h3>📊 Результаты опроса</h3>
            <div className="school-info">
              🏫 {currentData?.Пищеблок || foodBlockCode}
            </div>
          </div>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="tabs">
          <button 
            className={`tab-btn ${activeTab === 'children' ? 'active' : ''}`}
            onClick={() => setActiveTab('children')}
          >
            🧒 Дети
          </button>
          <button 
            className={`tab-btn ${activeTab === 'parents' ? 'active' : ''}`}
            onClick={() => setActiveTab('parents')}
          >
            👨‍👩‍👧 Родители
          </button>
        </div>
        
        {currentLoading ? (
          <div className="state-container">
            <div className="loading-spinner"></div>
            <div className="state-message">Загрузка данных...</div>
          </div>
        ) : currentError ? (
          <div className="state-container">
            <div className="state-icon">⚠️</div>
            <div className="state-message">{currentError}</div>
            <div className="state-hint">Проверьте код пищеблока или попробуйте позже</div>
            <button className="state-close-btn" onClick={onClose}>Закрыть</button>
          </div>
        ) : !currentData ? (
          <div className="state-container">
            <div className="state-icon">📭</div>
            <div className="state-message">Нет данных для отображения</div>
            <div className="state-hint">Для выбранного пищеблока нет данных опроса</div>
            <button className="state-close-btn" onClick={onClose}>Закрыть</button>
          </div>
        ) : (
          <>
            <div className="controls-bar">
              <button 
                className={`control-btn ${showMetrics ? 'active' : ''}`}
                onClick={() => setShowMetrics(!showMetrics)}
              >
                {showMetrics ? '📊 Скрыть показатели' : '📈 Показать показатели'}
              </button>
              <button className="control-btn export-btn" onClick={exportToExcel}>
                📎 Экспорт в Excel
              </button>
            </div>
            
            {showMetrics && (
              <div className="key-metrics-wrapper">
                {renderMetrics(currentData, totalRespondents, activeTab)}
              </div>
            )}
            
            <button 
              className={`toggle-details-btn ${showDetails ? 'active' : ''}`}
              onClick={() => setShowDetails(!showDetails)}
            >
              <span className="toggle-icon">{showDetails ? '▲' : '▼'}</span>
              {showDetails ? '📋 Скрыть детальные результаты' : '📋 Показать детальные результаты'}
            </button>
            
            {showDetails && (
              <div className="poll-results-body">
                {renderDetails(currentData, totalRespondents, currentMapping, currentQuestionsList)}
              </div>
            )}
          </>
        )}
        
        <div className="poll-results-footer">
          <button className="close-footer-btn" onClick={onClose}>Закрыть</button>
        </div>
      </div>

      <style>{`
        * {
          box-sizing: border-box;
        }
        
        .poll-results-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        
        .poll-results-container {
          background: #ffffff;
          border-radius: 8px;
          width: 95%;
          max-width: 1000px;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
          border: 1px solid #1a3a5c;
        }
        
        .poll-results-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 20px 24px;
          background: #1a3a5c;
          color: white;
          flex-shrink: 0;
          border-bottom: 3px solid #2c5f8a;
        }
        
        .poll-results-header h3 {
          margin: 0 0 6px 0;
          font-size: 18px;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        
        .school-info {
          font-size: 13px;
          opacity: 0.85;
          font-weight: 400;
        }
        
        .close-btn {
          background: rgba(255,255,255,0.15);
          border: none;
          font-size: 22px;
          cursor: pointer;
          padding: 0;
          width: 34px;
          height: 34px;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          transition: all 0.2s;
        }
        
        .close-btn:hover {
          background: rgba(255,255,255,0.25);
        }
        
        .tabs {
          display: flex;
          background: #f0f4f8;
          border-bottom: 1px solid #c8d6e5;
          flex-shrink: 0;
        }
        
        .tab-btn {
          flex: 1;
          padding: 12px 16px;
          background: none;
          border: none;
          font-size: 14px;
          font-weight: 600;
          color: #4a6a8a;
          cursor: pointer;
          transition: all 0.2s;
          position: relative;
          letter-spacing: 0.3px;
        }
        
        .tab-btn.active {
          color: #1a3a5c;
          background: white;
          border-bottom: 2px solid #1a3a5c;
        }
        
        .tab-btn:hover:not(.active) {
          background: #e8edf3;
          color: #1a3a5c;
        }
        
        .controls-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 20px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          gap: 12px;
          flex-wrap: wrap;
        }
        
        .control-btn {
          padding: 8px 16px;
          background: white;
          border: 1px solid #c8d6e5;
          border-radius: 4px;
          font-size: 13px;
          font-weight: 500;
          color: #1a3a5c;
          cursor: pointer;
          transition: all 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        
        .control-btn:hover {
          background: #e8edf3;
          border-color: #1a3a5c;
        }
        
        .control-btn.active {
          background: #1a3a5c;
          border-color: #1a3a5c;
          color: white;
        }
        
        .export-btn {
          background: #2c5f8a;
          border-color: #2c5f8a;
          color: white;
        }
        
        .export-btn:hover {
          background: #1a3a5c;
        }
        
        .key-metrics-wrapper {
          flex-shrink: 0;
          animation: fadeIn 0.3s ease;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .metrics-container {
          padding: 16px 20px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .metrics-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
          margin-bottom: 12px;
        }
        
        .metrics-grid:last-child {
          margin-bottom: 0;
        }
        
        .metric-card {
          background: white;
          border-radius: 6px;
          padding: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
          transition: all 0.2s;
          border: 1px solid #e2e8f0;
        }
        
        .metric-card:hover {
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
          border-color: #c8d6e5;
        }
        
        .metric-icon {
          font-size: 28px;
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f0f4f8;
          border-radius: 6px;
        }
        
        .metric-info {
          flex: 1;
        }
        
        .metric-value {
          font-size: 24px;
          font-weight: 700;
          color: #1a3a5c;
          line-height: 1.2;
        }
        
        .metric-label {
          font-size: 10px;
          color: #6c86a0;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-top: 2px;
        }
        
        .metric-sub {
          font-size: 10px;
          color: #94a8c0;
          margin-top: 2px;
        }
        
        .rating-card .metric-value {
          color: #c9a03d;
        }
        
        .satisfaction .metric-value {
          color: #2e7d64;
        }
        
        .dissatisfaction .metric-value {
          color: #c13b3b;
        }
        
        .warning .metric-value {
          color: #c9a03d;
        }
        
        .toggle-details-btn {
          padding: 12px 20px;
          background: #f0f4f8;
          border: none;
          border-top: 1px solid #e2e8f0;
          border-bottom: 1px solid #e2e8f0;
          font-size: 13px;
          font-weight: 500;
          color: #1a3a5c;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 0.2s;
        }
        
        .toggle-details-btn:hover {
          background: #e8edf3;
        }
        
        .toggle-details-btn.active {
          background: #e8edf3;
        }
        
        .toggle-icon {
          font-size: 11px;
        }
        
        .poll-results-body {
          flex: 1;
          overflow: auto;
          padding: 16px 20px;
        }
        
        .detail-block {
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          padding: 14px;
          margin-bottom: 12px;
          transition: box-shadow 0.2s;
        }
        
        .detail-block:hover {
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          border-color: #c8d6e5;
        }
        
        .detail-question {
          font-size: 13px;
          font-weight: 600;
          color: #1a3a5c;
          margin-bottom: 12px;
          padding-bottom: 6px;
          border-bottom: 1px solid #e2e8f0;
        }
        
        .detail-rating {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 10px;
        }
        
        .rating-stars {
          font-size: 18px;
          letter-spacing: 3px;
          color: #c9a03d;
        }
        
        .rating-value {
          font-size: 18px;
          font-weight: 700;
          color: #c9a03d;
          background: #fef8e7;
          padding: 4px 12px;
          border-radius: 4px;
        }
        
        .detail-answers {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        
        .answer-item {
          width: 100%;
        }
        
        .answer-header {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          margin-bottom: 5px;
          flex-wrap: wrap;
          gap: 8px;
        }
        
        .answer-text {
          font-size: 12px;
          color: #4a6a8a;
          flex: 1;
        }
        
        .answer-numbers {
          font-size: 11px;
          font-weight: 600;
          color: #2c5f8a;
        }
        
        .progress-bar {
          height: 6px;
          background: #e2e8f0;
          border-radius: 3px;
          overflow: hidden;
        }
        
        .progress-fill {
          height: 100%;
          background: #2c5f8a;
          border-radius: 3px;
          transition: width 0.3s ease;
        }
        
        .detail-simple-answer {
          font-size: 18px;
          font-weight: 700;
          color: #2c5f8a;
          text-align: center;
          padding: 8px;
          background: #f0f4f8;
          border-radius: 6px;
        }
        
        .simple .detail-simple-answer {
          font-size: 14px;
        }
        
        .poll-results-footer {
          padding: 12px 20px;
          border-top: 1px solid #e2e8f0;
          text-align: right;
          background: #f8fafc;
          flex-shrink: 0;
        }
        
        .close-footer-btn {
          background: #e2e8f0;
          color: #1a3a5c;
          border: none;
          padding: 6px 24px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 500;
          transition: all 0.2s;
        }
        
        .close-footer-btn:hover {
          background: #c8d6e5;
        }
        
        /* Состояния (загрузка, ошибка, пусто) */
        .state-container {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          text-align: center;
        }
        
        .state-icon {
          font-size: 64px;
          margin-bottom: 20px;
        }
        
        .state-message {
          font-size: 18px;
          font-weight: 600;
          color: #1a3a5c;
          margin-bottom: 12px;
        }
        
        .state-hint {
          font-size: 14px;
          color: #6c86a0;
          margin-bottom: 24px;
        }
        
        .state-close-btn {
          background: #2c5f8a;
          color: white;
          border: none;
          padding: 8px 32px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s;
        }
        
        .state-close-btn:hover {
          background: #1a3a5c;
        }
        
        .loading-spinner {
          width: 48px;
          height: 48px;
          border: 3px solid #e2e8f0;
          border-top-color: #2c5f8a;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
          margin-bottom: 20px;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        
        body.modal-open {
          overflow: hidden !important;
        }
        
        .poll-results-body::-webkit-scrollbar {
          width: 6px;
        }
        
        .poll-results-body::-webkit-scrollbar-track {
          background: #e2e8f0;
          border-radius: 3px;
        }
        
        .poll-results-body::-webkit-scrollbar-thumb {
          background: #94a8c0;
          border-radius: 3px;
        }
        
        .poll-results-body::-webkit-scrollbar-thumb:hover {
          background: #2c5f8a;
        }
        
        @media (max-width: 640px) {
          .poll-results-container {
            width: 100%;
            max-width: 100%;
            height: 100%;
            max-height: 100vh;
            border-radius: 0;
          }
          
          .poll-results-header {
            padding: 16px;
          }
          
          .poll-results-header h3 {
            font-size: 16px;
          }
          
          .tabs {
            padding: 0;
          }
          
          .tab-btn {
            padding: 10px;
            font-size: 13px;
          }
          
          .controls-bar {
            padding: 10px 16px;
          }
          
          .control-btn {
            padding: 6px 12px;
            font-size: 12px;
          }
          
          .metrics-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 8px;
          }
          
          .metric-card {
            padding: 10px;
          }
          
          .metric-icon {
            font-size: 24px;
            width: 36px;
            height: 36px;
          }
          
          .metric-value {
            font-size: 20px;
          }
          
          .metrics-container {
            padding: 12px;
          }
          
          .poll-results-body {
            padding: 12px;
          }
          
          .detail-block {
            padding: 10px;
          }
          
          .detail-question {
            font-size: 12px;
          }
          
          .answer-text {
            font-size: 11px;
          }
          
          .state-icon {
            font-size: 48px;
          }
          
          .state-message {
            font-size: 16px;
          }
        }
      `}</style>
    </div>
  );
};

export default PollResults;